import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { TextField } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useCreateInventorySupplier } from '../../../hooks/inventory/useCreateInventorySupplier';
import { useUpdateInventorySupplier } from '../../../hooks/inventory/useUpdateInventorySupplier';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';

export default function InventorySupplierForm({ successCallback = null, supplier = null, mode = "create", open, onClose }) {

    const { t } = useTranslation('common'); 

    const [name, setName] = React.useState(supplier ? supplier.name : "");
    const [contactPerson, setContactPerson] = React.useState(supplier ? supplier.contact_person : "");
    const [email, setEmail] = React.useState(supplier ? supplier.email : "");
    const [contactNumber, setContactNumber] = React.useState(supplier ? supplier.contact_number : "");
    const [address, setAddress] = React.useState(supplier ? supplier.address : "");

    const { create } = useCreateInventorySupplier(successCallback);
    const { update } = useUpdateInventorySupplier(successCallback);

    const { showSnackbar } = useSnackbar();

    React.useEffect(() => {
        // reset field when there are changes (e.g. when opening edit)
        setName(supplier ? supplier.name : "");
        setContactPerson(supplier ? supplier.contact_person : "");
        setEmail(supplier ? supplier.email : "");
        setContactNumber(supplier ? supplier.contact_number : "");
        setAddress(supplier ? supplier.address : "");
    }, [supplier, open]);

    const handleSave = async () => {
        if (!name.trim()) {
            showSnackbar(t('inventory.suppliers.messages.name_required'), "warning");
            return;
        }
        if (!contactPerson.trim()) {
            showSnackbar(t('inventory.suppliers.messages.contact_person_required'), "warning");
            return;
        }
        if (!email.trim()) {
            showSnackbar(t('inventory.suppliers.messages.email_required'), "warning");
            return;
        }
        if (!contactNumber.trim()) {
            showSnackbar(t('inventory.suppliers.messages.contact_number_required'), "warning");
            return;
        }

        try 
        {
            if (mode === "create") {
                await create({ 
                    name: name,
                    contact_person: contactPerson,
                    email: email,
                    contact_number: contactNumber,
                    address: address 
                });
                showSnackbar(t('inventory.suppliers.messages.create_success'), "success");
            } else if (mode === "edit" && supplier) {
                await update(supplier.id, { 
                    name: name,
                    contact_person: contactPerson,
                    email: email,
                    contact_number: contactNumber,
                    address: address 
                 });
                showSnackbar(t('inventory.suppliers.messages.update_success'), "success");
            }
            setName("");   // clear field after save
            setContactPerson("");
            setEmail("");
            setContactNumber("");
            setAddress("");
            onClose();     // close dialog
        } catch (err) {
            console.log("err.response", err.response);
            if (err.response?.status === 409) {
                showSnackbar(t('inventory.suppliers.messages.email_duplicate'), "error");
            } else {
                showSnackbar(t('inventory.suppliers.messages.save_fail'), "error");
            }
        }

    };

    const handleCancel = () => {
        setName("");   // clear field on cancel
        setContactPerson("");
        setEmail("");
        setContactNumber("");
        setAddress("");
        onClose();
    };

    return (
        <>
        <Dialog onClose={handleCancel} open={open} disableRestoreFocus>

        <DialogTitle>{mode === "create" ? "Add Supplier" : "Edit Supplier"}</DialogTitle>

        <DialogContent dividers>
            <Stack spacing={1}>
            <TextField
                label="Supplier Name"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
            />
            <TextField
                label="Contact Person"
                variant="outlined"
                value={contactPerson}
                onChange={(e) => setContactPerson(e.target.value)}
                fullWidth
            />
            <TextField
                label="E-mail"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
            />
            <TextField
                label="Contact Number"
                variant="outlined"
                value={contactNumber}
                onChange={(e) => setContactNumber(e.target.value)}
                fullWidth
            />
            <TextField
                label="Address"
                variant="outlined"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                fullWidth
                multiline
                rows={2}
            />
            </Stack>
        </DialogContent>

        <DialogActions>
            <Button onClick={handleSave}>
                Save
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
        </DialogActions>
        </Dialog>
        </>
    );
}